import React from 'react'
import {createUseStyles} from 'react-jss'

import Layout from '../components/layouts/layout'
import Picture from '../components/picture'
import Button8 from '../components/button8'
import BasicPageHeading from '../components/basic-page-heading'
import aframeLogo from '../../img/engines/aframe.png'
import threeJsLogo from '../../img/engines/threejs.png'
import babylonLogo from '../../img/engines/babylon.png'
import sumerianLogo from '../../img/engines/sumerian-rough.png'
import playCanvasLogo from '../../img/engines/playcanvas.png'
import aframeLogoWebp from '../../img/engines/aframe.webp'
import threeJsLogoWebp from '../../img/engines/threejs.webp'
import babylonLogoWebp from '../../img/engines/babylon.webp'
import sumerianLogoWebp from '../../img/engines/sumerian-rough.webp'
import playCanvasLogoWebp from '../../img/engines/playcanvas.webp'
import {combine} from '../styles/classname-utils'
import {gray4, gray5, moonlight} from '../styles/brand-colors'
import {MOBILE_VIEW_OVERRIDE, SMALL_MONITOR_VIEW_OVERRIDE} from '../styles/constants'

const useStyles = createUseStyles({
  headingContainer: {
    'height': 'fit-content',
    'backgroundColor': moonlight,
    'display': 'flex',
    'flexDirection': 'column',
    'justifyContent': 'center',
    'alignItems': 'center',
  },
  heading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '100%',
    marginBottom: '1rem',
    textAlign: 'center',
  },
  headingVideo: {
    'maxWidth': '40rem',
    'maxHeight': '90%',
  },
  sectionHeadingContainer: {
    'paddingBottom': '1rem',
    'justifyContent': 'center',
    '& *': {
      textAlign: 'center',
    },
  },
  learningCardSection: {
    paddingBottom: '0.5rem',
  },
  frameworksSection: {
    backgroundColor: moonlight,
  },
  learningCard: {
    'justifyContent': 'center',
    'margin': 'auto',
    '& > div': {
      maxHeight: '90%',
      padding: '0 1rem',
      marginBottom: '3.5em',
      [MOBILE_VIEW_OVERRIDE]: {
        padding: 0,
        marginBottom: '1.5em',
      },
    },
    '& p': {
      margin: 0,
      paddingTop: '0.5rem',
    },

  },
  engineBoxContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  engineBox: {
    'maxWidth': '12rem',
    'maxHeight': '12rem',
    'boxShadow': `0px 0px 5px -2px ${gray5}`,
    'margin': '1rem',
    'padding': '1rem',
    '&:hover': {
      transform: 'scale(1.04)',
      transition: 'all 0.08s linear',
    },
    [SMALL_MONITOR_VIEW_OVERRIDE]: {
      maxWidth: '10rem',
      maxHeight: '10rem',
      margin: '0.5rem',
      padding: '0.5rem',
    },
  },
  engineIcon: {
    width: '100%',
    height: 'auto',
  },
  moreProjectContainer: {
    color: gray4,
  },
})

export default () => {
  const classes = useStyles()
  const liveLearningSessionUrl =
    'https://us02web.zoom.us/webinar/register/1816174025066/WN_RJ3O1p7nSiW9QckyCWEFOw'
  return (
    <Layout
      title='Tutorials - Learn How to Develop WebAR Projects'
      description='Learn how to use 8th Wall’s powerful augmented reality SDK to create Web AR projects using Face Effects, World Tracking, Image Targets and more.'
    >
      <BasicPageHeading>
        Tutorials
      </BasicPageHeading>

      <section className={classes.headingContainer}>
        <div className={classes.heading}>
          <h2>Getting Started with 8th Wall</h2>
          <h3>Get up and running quickly with 8th Wall development in 5 minutes ⏱</h3>
        </div>

        <div className={combine('embed-responsive embed-responsive-16by9', classes.headingVideo)}>
          <iframe
            src='https://www.youtube.com/embed/-iAhNh_qD9I?rel=0&modestbranding=1'
            className='embed-responsive-item'
            frameBorder='0'
            allow='autoplay; encrypted-media'
            allowFullScreen
          />
        </div>
      </section>

      <section className={classes.learningCardSection}>
        <div className={combine('row', classes.sectionHeadingContainer)}>
          <div className='col-lg-8'>
            <h2>Live Learning Sessions</h2>
            <h3>Check out these deep dives into various aspects of 8th Wall development.</h3>
          </div>
        </div>

        <div className={combine('row', classes.learningCard)}>
          <div className='col-lg-4 col-md-6 col-sm-12'>
            <div className='embed-responsive embed-responsive-16by9'>
              <iframe
                src='https:///www.youtube.com/embed/flcck-rgVnc?rel=0&modestbranding=1'
                className='embed-responsive'
                frameBorder='0'
                allow='autoplay; encrypted-media'
                allowFullScreen
              />
            </div>
            <p>We cover some image tracking and world tracking updates, then a full walkthrough of how to
              develop with 8th Wall Cloud Editor.
            </p>
          </div>
          <div className='col-lg-4 col-md-6 col-sm-12'>
            <div className='embed-responsive embed-responsive-16by9'>
              <iframe
                src='https://www.youtube.com/embed/V4NXQ5_3Wak?rel=0&modestbranding=1'
                className='embed-responsive'
                frameBorder='0'
                allow='autoplay; encrypted-media'
                allowFullScreen
              />
            </div>
            <p>We introduce the 8th Wall Playground sample projects and show how to
              develop Face Effects projects that use Media Recorder.
            </p>
          </div>
        </div>

        <div className={combine('row', classes.learningCard)}>
          <div className='col-lg-4 col-md-6 col-sm-12'>
            <div className='embed-responsive embed-responsive-16by9'>
              <iframe
                src='https://www.youtube.com/embed/QENrIfJfOAA?rel=0&modestbranding=1'
                className='embed-responsive'
                frameBorder='0'
                allow='autoplay; encrypted-media'
                allowFullScreen
              />
            </div>
            <p>We highlight recent improvements to Cloud Editor, then walk through the custom domain set up process.
            </p>
          </div>
          <div className='col-lg-4 col-md-6 col-sm-12'>
            <div className='embed-responsive embed-responsive-16by9'>
              <iframe
                src='https://www.youtube.com/embed/wiga9zGesZY?rel=0&modestbranding=1'
                className='embed-responsive'
                frameBorder='0'
                allow='autoplay; encrypted-media'
                allowFullScreen
              />
            </div>
            <p>You will learn how to customize loading screens and create flat, cylindrical and conical Image Targets.
            </p>
          </div>
        </div>

        <div className={combine('row', classes.learningCard)}>
          <div className='col-lg-4 col-md-6 col-sm-12'>
            <div className='embed-responsive embed-responsive-16by9'>
              <iframe
                src='https://www.youtube.com/embed/wZzwWfEdNNU?rel=0&modestbranding=1'
                className='embed-responsive'
                frameBorder='0'
                allow='autoplay; encrypted-media'
                allowFullScreen
              />
            </div>
            <p>Learn how to create convincingly realistic WebAR experiences using 8th Wall that utilize Realtime Reflections.
            </p>
          </div>
          <div className='col-lg-4 col-md-6 col-sm-12'>
            <div className='embed-responsive embed-responsive-16by9'>
              <iframe
                src='https://www.youtube.com/embed/Pt65QmzSQx4?rel=0&modestbranding=1'
                className='embed-responsive'
                frameBorder='0'
                allow='autoplay; encrypted-media'
                allowFullScreen
              />
            </div>
            <p>Learn how to add Google Analytics to your 8th Wall-hosted projects.
            </p>
          </div>
        </div>

        <div className={combine('row', classes.learningCard)}>
          <div className='col-lg-4 col-md-6 col-sm-12'>
            <div className='embed-responsive embed-responsive-16by9'>
              <iframe
                src='https://www.youtube.com/embed/aXfWgZ_cJm8?rel=0&modestbranding=1'
                className='embed-responsive'
                frameBorder='0'
                allow='autoplay; encrypted-media'
                allowFullScreen
              />
            </div>
            <p>Learn about various types of WebAR Holograms and how to add them to your 8th Wall-hosted projects.
            </p>
          </div>
          <div className='col-lg-4 col-md-6 col-sm-12'>
            <div className='embed-responsive embed-responsive-16by9'>
              <iframe
                src='https://www.youtube.com/embed/tx3_Sz7TAso?rel=0&modestbranding=1'
                className='embed-responsive'
                frameBorder='0'
                allow='autoplay; encrypted-media'
                allowFullScreen
              />
            </div>
            <p>Learn how to customize the loading screen in your 8th Wall-hosted projects.
            </p>
          </div>
        </div>

        <div className={combine('row', classes.learningCard)}>
          <div className='col-lg-4 col-md-6 col-sm-12'>
            <div className='embed-responsive embed-responsive-16by9'>
              <iframe
                src='https://www.youtube.com/embed/BMVmxy9UtFo?rel=0&modestbranding=1'
                className='embed-responsive'
                frameBorder='0'
                allow='autoplay; encrypted-media'
                allowFullScreen
              />
            </div>
            <p>Learn how to develop WebAR with video textures in 8th Wall-hosted projects.
            </p>
          </div>
          <div className='col-lg-4 col-md-6 col-sm-12'>
            <div className='embed-responsive embed-responsive-16by9'>
              <iframe
                src='https://www.youtube.com/embed/VwQk4msRPs4?rel=0&modestbranding=1'
                className='embed-responsive'
                frameBorder='0'
                allow='autoplay; encrypted-media'
                allowFullScreen
              />
            </div>
            <p>Learn advanced techniques for implementing image targets in your 8th Wall project.
            </p>
          </div>
        </div>

        <div className={combine('row', classes.learningCard)}>
          <div className='col-lg-4 col-md-6 col-sm-12'>
            <div className='embed-responsive embed-responsive-16by9'>
              <iframe
                src='https://www.youtube.com/embed/Lh6Ypg_4ZHo?rel=0&modestbranding=1'
                className='embed-responsive'
                frameBorder='0'
                allow='autoplay; encrypted-media'
                allowFullScreen
              />
            </div>
            <p>Learn advanced Cloud Editor development and debugging techniques.
            </p>
          </div>
          <div className='col-lg-4 col-md-6 col-sm-12' />
        </div>
      </section>

      <section className={classes.frameworksSection}>
        <div className={combine('row', classes.sectionHeadingContainer)}>
          <div className='col-lg-8'>
            <h2>Get started with our supported 3D Frameworks</h2>
          </div>
        </div>

        <div className={combine('row', classes.engineBoxContainer)}>

          <div className={classes.engineBox}>
            <a target='_blank' rel='noopener noreferrer' href='https://aframe.io/'>
              <Picture
                webpSrc={aframeLogoWebp}
                pngSrc={aframeLogo}
                className={classes.engineIcon}
              />
            </a>
          </div>

          <div className={classes.engineBox}>
            <a target='_blank' rel='noopener noreferrer' href='https://threejs.org/'>
              <Picture
                webpSrc={threeJsLogoWebp}
                pngSrc={threeJsLogo}
                className={classes.engineIcon}
              />
            </a>
          </div>

          <div className={classes.engineBox}>
            <a target='_blank' rel='noopener noreferrer' href='https://www.babylonjs.com/'>
              <Picture
                webpSrc={babylonLogoWebp}
                pngSrc={babylonLogo}
                className={classes.engineIcon}
              />
            </a>
          </div>

          <div className={classes.engineBox}>
            <a target='_blank' rel='noopener noreferrer' href='https://playcanvas.com/'>
              <Picture
                webpSrc={playCanvasLogoWebp}
                pngSrc={playCanvasLogo}
                className={classes.engineIcon}
              />
            </a>
          </div>

          <div className={classes.engineBox}>
            <a target='_blank' rel='noopener noreferrer' href='https://aws.amazon.com/sumerian/'>
              <Picture
                webpSrc={sumerianLogoWebp}
                pngSrc={sumerianLogo}
                className={classes.engineIcon}
              />
            </a>
          </div>

        </div>
      </section>

      <section className='purple'>
        <div className='row justify-content-center text-center'>
          <h2 className='white'>Join the next 8th Wall Live Learning Session</h2>
          <div className='col-12 text-center'>
            <a target='_blank' rel='noopener noreferrer' href={liveLearningSessionUrl}>
              <Button8 secondary>
                Register
              </Button8>
            </a>
          </div>

        </div>
      </section>
    </Layout>
  )
}
